import React, { useId } from "react"

import { formatFiatCurrency } from "util/formatter"

// The value a MoneyAmountInput reads and emits: an amount in that currency's MINOR units
// (cents) plus the currency it is written in. `amountCents` is optional so an empty field is
// representable (undefined = nothing entered yet).
export interface MoneyAmountValue {
  amountCents?: number
  currency: string
}

export interface MoneyAmountInputProps {
  value: MoneyAmountValue
  onChange: (next: MoneyAmountValue) => void
  // Allowed currency codes (e.g. the tenant's enabled `rcoin` codes from admin settings).
  currencies: string[]
  label?: string
  placeholder?: string
  disabled?: boolean
  required?: boolean
  // Lock the currency selector (e.g. when the currency is fixed by a parent repeater row).
  currencyDisabled?: boolean
  // Render only the amount input + preview, no currency selector — for amount cells whose
  // currency is owned by a parent row. `value.currency` is still used to format the preview.
  hideCurrency?: boolean
  // Offer an empty "—" currency option (for optional/base-fallback usage).
  allowEmptyCurrency?: boolean
  emptyCurrencyLabel?: string
  // Show the formatted "€500.00" preview under the input. Defaults to true.
  showPreview?: boolean
  id?: string
  // Styling hooks — everything is overridable so a parent can restyle without forking.
  className?: string
  inputClassName?: string
  selectClassName?: string
  labelClassName?: string
}

// AP-aligned defaults. daisyUI `*-bordered` + a subtle tint; the bare classes render the light
// ("white") theme, the `dark:` variants the dark theme — so the component is theme-aware with no
// extra work. Every class is overridable via props.
const DEFAULT_INPUT_CLASS = "w-full input input-bordered input-sm bg-[rgba(0,0,0,0.05)] dark:bg-[rgba(0,0,0,0.2)]"
const DEFAULT_SELECT_CLASS =
  "select select-bordered select-sm bg-[rgba(0,0,0,0.05)] dark:bg-[rgba(0,0,0,0.2)] [&>option]:bg-base-100 [&>option]:dark:bg-[#1a1a2e]"
const DEFAULT_LABEL_CLASS = "label-text dark:text-gray-300"

/**
 * Reusable money field with an attached currency selector. The admin enters an amount in the
 * currency's MINOR units (cents) and picks the currency from the allowed list; the component
 * emits `{ amountCents, currency }` and shows a clean formatted preview (e.g. `€500.00`) so the
 * raw cents still read sensibly. Currency-tagged amounts avoid FX-converted numbers on the FE.
 */
export const MoneyAmountInput = ({
  value,
  onChange,
  currencies,
  label,
  placeholder = "Amount (cents)",
  disabled = false,
  required = false,
  currencyDisabled = false,
  hideCurrency = false,
  allowEmptyCurrency = false,
  emptyCurrencyLabel = "—",
  showPreview = true,
  id,
  className = "",
  inputClassName = DEFAULT_INPUT_CLASS,
  selectClassName = DEFAULT_SELECT_CLASS,
  labelClassName = DEFAULT_LABEL_CLASS
}: MoneyAmountInputProps) => {
  const autoId = useId()
  const fieldId = id ?? autoId

  const handleAmountChange = (raw: string) => {
    const amountCents = raw === "" ? undefined : Math.max(0, Math.trunc(Number(raw)))
    onChange({ ...value, amountCents: Number.isNaN(amountCents as number) ? undefined : amountCents })
  }

  const handleCurrencyChange = (currency: string) => {
    onChange({ ...value, currency })
  }

  const preview =
    showPreview && value.currency && value.amountCents !== undefined ? formatFiatCurrency(value.amountCents, "", value.currency) : ""

  const currencyUnlisted = !hideCurrency && !!value.currency && !currencies.includes(value.currency)

  return (
    <div className={`form-control ${className}`}>
      {label && (
        <label htmlFor={fieldId} className="label py-1">
          <span className={labelClassName}>{label}</span>
        </label>
      )}
      <div className="flex gap-2">
        <input
          id={fieldId}
          type="number"
          min={0}
          step={1}
          className={inputClassName}
          placeholder={placeholder}
          value={value.amountCents ?? ""}
          onChange={(e) => handleAmountChange(e.target.value)}
          disabled={disabled}
          required={required}
        />
        {!hideCurrency && (
          <select
            className={selectClassName}
            value={value.currency}
            onChange={(e) => handleCurrencyChange(e.target.value)}
            disabled={disabled || currencyDisabled}
            aria-label="Currency"
          >
            {allowEmptyCurrency && <option value="">{emptyCurrencyLabel}</option>}
            {currencyUnlisted && (
              <option value={value.currency} disabled>
                {value.currency}
              </option>
            )}
            {currencies.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        )}
      </div>
      {preview && <span className="mt-1 text-xs text-emerald-500 dark:text-emerald-400">= {preview}</span>}
    </div>
  )
}
